import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/lib/supabase"
import { useAuth } from "@/components/auth-context"

// ─── Tipos ───────────────────────────────────────────────────────────────────

export type PlanName = "free" | "pro" | "teams"

export interface PlanFeatures {
    invoices: boolean
    ai: boolean
    reports_advanced: boolean
    billable_rates: boolean
    team_dashboard: boolean
    custom_fields: boolean
    api_webhooks: boolean
    timesheet_approval: boolean
    role_management: boolean
    performance_reports: boolean
    consolidated_billing: boolean
    custom_domain: boolean
    sso_auth: boolean
    priority_support: boolean
    vip_support: boolean
}

export interface Plan {
    id: string
    name: PlanName
    display_name: string
    stripe_price_monthly: string | null
    stripe_price_annual: string | null
    paddle_product_id?: string | null
    paddle_price_monthly_id?: string | null
    paddle_price_annual_id?: string | null
    lemonsqueezy_product_id?: string | null
    lemonsqueezy_variant_monthly?: string | null
    lemonsqueezy_variant_annual?: string | null
    max_users: number | null        // null = ilimitado
    max_projects: number | null     // null = ilimitado
    max_clients: number | null      // null = ilimitado
    history_days: number | null     // null = ilimitado
    features: PlanFeatures
    price_monthly_cents: number
    price_annual_cents: number
    sort_order: number
    is_active: boolean
}

export interface Subscription {
    id: string
    organization_id: string
    plan_id: string
    stripe_customer_id: string | null
    stripe_subscription_id: string | null
    lemonsqueezy_subscription_id?: string | null
    lemonsqueezy_customer_id?: string | null
    lemonsqueezy_variant_id?: string | null
    lemonsqueezy_order_id?: string | null
    status: "active" | "trialing" | "past_due" | "canceled"
    billing_cycle: "monthly" | "annual"
    seat_count: number
    current_period_end: string | null
    cancel_at_period_end: boolean
    /** URL del portal de cliente de Lemon Squeezy. Se refresca con cada webhook. */
    customer_portal_url?: string | null
}

export type PlanFeatureKey = keyof PlanFeatures
export type PlanLimitKey = "max_users" | "max_projects" | "max_clients"

// ─── Constantes ─────────────────────────────────────────────────────────────

export const PLAN_LEVELS: Record<PlanName, number> = {
    free: 0,
    pro: 1,
    teams: 2,
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export function usePlan() {
    const { organization } = useAuth()

    // Cargar todos los planes disponibles (catálogo)
    const { data: plans = [], isLoading: plansLoading } = useQuery<Plan[]>({
        queryKey: ["plans"],
        queryFn: async () => {
            const { data, error } = await supabase
                .from("plans")
                .select("*")
                .order("sort_order")
            if (error) throw error
            
            // ─── Overrides para asegurar consistencia con el marketing ───────
            return (data as Plan[]).map(p => {
                const features: PlanFeatures = { ...p.features }
                
                // ─── Límites de recursos (fuente: lógica de negocio, no DB) ─────
                // Los precios se leen directamente de Supabase → se sincronizan con Lemon Squeezy.
                // Solo sobreescribimos los límites operativos que no se almacenan en DB.
                if (p.name === "pro" || p.name === "teams") {
                    p.max_users = -1       // ilimitado
                    p.max_projects = -1   // ilimitado
                    p.max_clients = -1    // ilimitado
                    p.history_days = null // historial completo
                }
                
                // Free: Solo lo básico (actualmente casi nada en features JSON)
                if (p.name === "free") {
                    features.invoices = false
                    features.ai = false
                    features.reports_advanced = false
                    features.billable_rates = false
                    features.team_dashboard = false
                    features.timesheet_approval = false
                    features.custom_fields = false
                    features.api_webhooks = false
                    features.role_management = false
                    features.performance_reports = false
                    features.consolidated_billing = false
                    features.custom_domain = false
                    features.sso_auth = false
                    features.priority_support = false
                    features.vip_support = false
                }
                
                // Pro: +AI, +Reports, +Rates, +Fields
                if (p.name === "pro") {
                    features.invoices = true
                    features.ai = true
                    features.reports_advanced = true
                    features.billable_rates = true
                    features.custom_fields = true
                    features.priority_support = true
                    
                    // Lo demás desactivado (exclusivo para Teams)
                    features.team_dashboard = false
                    features.timesheet_approval = false
                    features.api_webhooks = false
                    features.role_management = false
                    features.performance_reports = false
                    features.consolidated_billing = false
                    features.custom_domain = false
                    features.sso_auth = false
                    features.vip_support = false
                }
                
                // Teams: Garantizar que tiene absolutamente todo habilitado
                if (p.name === "teams") {
                    features.invoices = true
                    features.ai = true
                    features.reports_advanced = true
                    features.billable_rates = true
                    features.team_dashboard = true
                    features.custom_fields = true
                    features.api_webhooks = true
                    features.timesheet_approval = true
                    features.role_management = true
                    features.performance_reports = true
                    features.consolidated_billing = true
                    features.custom_domain = true
                    features.sso_auth = true
                    features.priority_support = true
                    features.vip_support = true
                }
                
                return { ...p, features }
            })
        },
        staleTime: 1000 * 60 * 60, // 1 hora — los planes cambian poco
    })

    // Cargar la suscripción de la organización activa
    const { data: subscription, isLoading: subscriptionLoading } = useQuery<Subscription | null>({
        queryKey: ["subscription", organization?.id],
        queryFn: async () => {
            if (!organization?.id) return null
            const { data, error } = await supabase
                .from("subscriptions")
                .select("*")
                .eq("organization_id", organization.id)
                .single()
            if (error && error.code !== "PGRST116") throw error // PGRST116 = not found
            return (data as Subscription) ?? null
        },
        enabled: !!organization?.id,
        staleTime: 1000 * 60 * 5, // 5 minutos
    })

    // Plan actual (si no hay suscripción = Free)
    // Usamos useMemo para evitar recalcular innecesariamente y manejar estados de carga
    const { currentPlan, isFreePlan, isProPlan, isTeamsPlan } = (() => {
        const freePlan = plans.find(p => p.name === "free") ?? null;
        const current = subscription
            ? plans.find(p => p.id === subscription.plan_id) ?? freePlan
            : freePlan;
        
        return {
            currentPlan: current,
            isFreePlan: current?.name === "free" || !current,
            isProPlan: current?.name === "pro",
            isTeamsPlan: current?.name === "teams",
        }
    })();

    // ─── Helpers de acceso ────────────────────────────────────────────────

    /** ¿El plan actual tiene esta feature activada? */
    const canUse = (feature: PlanFeatureKey): boolean => {
        if (!currentPlan) return false
        return currentPlan.features[feature] === true
    }

    /** ¿El plan actual tiene un límite en este recurso? */
    const getLimit = (resource: PlanLimitKey): number | null => {
        return currentPlan?.[resource] ?? null
    }

    /** ¿El plan actual puede acceder al historial de esta fecha? */
    const isDateInHistory = (date: Date): boolean => {
        if (!currentPlan?.history_days) return true // ilimitado
        const cutoff = new Date()
        cutoff.setDate(cutoff.getDate() - currentPlan.history_days)
        return date >= cutoff
    }

    /** ¿El plan es al menos de este nivel? */
    const isPlanAtLeast = (plan: PlanName): boolean => {
        const current = (currentPlan?.name ?? "free").toLowerCase() as PlanName;
        const target = plan.toLowerCase() as PlanName;
        return (PLAN_LEVELS[current] ?? 0) >= (PLAN_LEVELS[target] ?? 0);
    };

    return {
        // Datos
        plans,
        currentPlan,
        subscription,
        isLoading: plansLoading || subscriptionLoading,

        // Nombre del plan actual
        planName: (currentPlan?.name ?? "free") as PlanName,
        isFreePlan,
        isProPlan,
        isTeamsPlan,

        // Estado de suscripción
        isActive: !subscription || subscription.status === "active" || subscription.status === "trialing",
        isPastDue: subscription?.status === "past_due",

        // Funciones de control de acceso
        canUse,
        getLimit,
        isDateInHistory,
        isPlanAtLeast,
    }
}

